// useEffectiveClassified — the single source of the `ClassifiedPlan` the
// cooking panes render from.
//
// Policy:
//   - When the server has answered (`serverClassified` is non-null) it is
//     authoritative — its states (and the IP-bearing soft/hard banner)
//     win.
//   - Before the first tick, or when the functions are absent (plain
//     `vite dev`) so the server never answers, fall back to a local
//     wall-clock classification of the immutable schedule so tasks still
//     move upcoming → active → overrun and their clocks appear.
//   - Either way, optimistic completion is overlaid: a task the cook has
//     just checked flips to `done` at once, then the server reconciles.
//
// Re-samples the corrected clock once per second (like the dials and the
// timeline) so local transitions happen on time without a server tick.

import { useMemo } from 'react';
import type { ClassifiedPlan, ScheduleEntry } from '@cookchow/shared';
import { useNow } from './useNow';
import { classifyLocal, withOptimisticCompletion } from './classifyLocal';

export function useEffectiveClassified(
  schedule: readonly ScheduleEntry[],
  serverClassified: ClassifiedPlan | null,
  completedTaskIds: readonly string[],
  clockOffset: number,
): ClassifiedPlan {
  const nowMs = useNow(clockOffset);
  return useMemo(() => {
    const base = serverClassified ?? classifyLocal(schedule, nowMs, completedTaskIds);
    return withOptimisticCompletion(base, completedTaskIds);
  }, [schedule, serverClassified, completedTaskIds, nowMs]);
}
